import React, {Fragment} from 'react'
import Reflux from 'reflux-react-16'
import _ from 'lodash'

import time from '../../server/utils/time.js'

import OrderStore from '../stores/OrderStore.js'
import OrderActions from '../actions/OrderActions.js'
import AddressStore from '../../src/stores/AddressStore.js'

import {ScanFrame} from '../components/index.js'
import {Alert, Badge, Button, Card, Colors, H2, H4, H5, IconButton, ListItem, P, Panel, Popup, Row, S1, S2} from '../../src/components/UI/index.js'
import {emit} from 'messenger/socketio/socketEmit.js'
import geolocation from '@utils/geolocation.js'
import utils from '../utils/utils.js'
import questionOptions from '../utils/questionOptions.js'
import stopBundles from '../../utils/stopBundles.js'
import moment from 'moment'
import dbCodes from 'server/dbCodes.js'
import {resolveMessengerRouteSettings, canAddOrderToRouteWithWarningSetting} from '../../utils/routeAdjustability.js'
import {isRetourOrder} from '../../utils/retourAddresses.js'
import AddressTypeIcon from '../../src/components/orders/AddressTypeIcon.js'

class Scan extends Reflux.Component {
    constructor(props) {
        super(props)

        this.state = {
            scannedCollo: null,
            scannedColli: null,
            scannedOrder: null,
            scannedOrders: null,
            loading: false,
            message: '',
            warning: '',
            error: '',
            errors: []
        }

        this.stores = [OrderStore, AddressStore]
    }

    async onScan(barcode, forceNoScan = false, preferredOrderIds = []) {
        const {route, isScanning} = this.state

        this.setState({scannedOrder: null, scannedOrders: null, scannedCollo: null, scannedColli: null, loading: true, message: '', warning: '', error: '', errors: []})

        // Sort colli by deliveryAddressIndex in descending order to prioritize the most delivery addresses because it's more likely to scan a barcode that has a delivery address index
        const sortedColli = [...(route?.colli ?? [])].sort((a, b) => (b.deliveryAddressIndex ?? -1) - (a.deliveryAddressIndex ?? -1))

        let colli = sortedColli?.filter((collo) => collo.barcode === barcode)

        try {
            const response = await emit('orders.findByBarcode', barcode)

            let foundOrders = response.filter((order) => order.date === time.today())

            if (foundOrders.length === 0) {
                foundOrders = response
            }

            if (preferredOrderIds.length > 0) {
                const preferredIds = preferredOrderIds.map((id) => `${id}`)

                const preferredFoundOrders = foundOrders.filter((order) => preferredIds.includes(`${order._id}`))

                if (preferredFoundOrders.length > 0) {
                    foundOrders = preferredFoundOrders
                } else {
                    const preferredResponseOrders = response.filter((order) => preferredIds.includes(`${order._id}`))

                    if (preferredResponseOrders.length > 0) {
                        foundOrders = preferredResponseOrders
                    }
                }
            }

            const getBarcodeAddress = (order) => {
                const collo = order.colli.find((c) => c.barcode === barcode)
                return order.addresses[collo?.deliveryAddressIndex ?? collo?.pickupAddressIndex]
            }

            const isPickupDeliveryPair = foundOrders.length === 2 && (() => {
                const types = foundOrders.map((order) => getBarcodeAddress(order)?.type)
                return types.filter((type) => type === 'pickup').length === 1 && types.filter((type) => type === 'delivery').length === 1
            })()

            let toBeScannedOrders = []

            if (foundOrders.length === 1) {
                toBeScannedOrders = [foundOrders[0]]
            } else if (foundOrders.length > 1) {
                if (isPickupDeliveryPair) {
                    toBeScannedOrders = foundOrders.toSorted((orderA, orderB) => {
                        const addressA = getBarcodeAddress(orderA)
                        const addressB = getBarcodeAddress(orderB)

                        const aNumber = addressA?.type === 'pickup' ? 1 : 2
                        const bNumber = addressB?.type === 'pickup' ? 1 : 2

                        return aNumber - bNumber
                    })
                } else {
                    const selectedOrder = await this.popup.openOptions('Er zijn meerdere ritten gevonden voor deze barcode', 'selecteer de rit die je wilt scannen', foundOrders.sort((orderA, orderB) => {
                        const addressA = getBarcodeAddress(orderA)
                        const addressB = getBarcodeAddress(orderB)

                        const aNumber = addressA?.type === 'pickup' ? 1 : 2
                        const bNumber = addressB?.type === 'pickup' ? 1 : 2

                        return aNumber - bNumber
                    }).map((order) => {
                        const address = getBarcodeAddress(order)

                        return (
                            <ListItem key={order._id} onClick={() => this.popup.onClickOption({value: order._id})} style={{padding: '8px 0'}}>
                                <S1 style={{width: 'fit-content', marginRight: 12}}>{moment(order.date).format('YYYY-MM-DD')}</S1>

                                <div style={{flex: 2, display: 'flex', alignItems: 'center'}}>
                                    {address ?
                                        <AddressTypeIcon address={address} isRetour={isRetourOrder(order)} style={{marginRight: 8}} /> :
                                        null}
                                    <P>
                                        {address ?
                                            `${(address.isBusiness && address.name) ?
                                                address.name :
                                                `${address.street} ${address.nr}${address.addition}`}` :
                                            order.trackTrace
                                        }
                                    </P>
                                </div>
                                <Badge style={{width: 'fit-content'}}>{dbCodes.status[order.status]}</Badge>
                            </ListItem>
                        )
                    }))
                    if (!selectedOrder) return

                    const selectedOrderObject = foundOrders.find((order) => order._id === selectedOrder.value)

                    if (selectedOrderObject) {
                        toBeScannedOrders = [selectedOrderObject]
                    }
                }
            } else if (response[0]) {
                toBeScannedOrders = [response[0]]
            }

            // When re-loading an already started route, scanning should not update collo status
            const restartLoadingStartedRoute = !!(isScanning && route?.started)

            const scannedOrders = []

            for (const toBeScannedOrder of toBeScannedOrders) {
                const orderIsInCurrentRoute = !!toBeScannedOrder && (toBeScannedOrder.routeId === route?._id || colli?.some((c) => c.orderId === toBeScannedOrder._id))
                const shouldUseNoScan = forceNoScan || !orderIsInCurrentRoute || restartLoadingStartedRoute
                const scanPayload = shouldUseNoScan ?
                    {id: toBeScannedOrder._id, barcode, noScan: true} :
                    {id: toBeScannedOrder._id, barcode}

                const order = await emit('orders.scan', scanPayload)
                const orderAlreadyInColli = colli?.find((c) => c.orderId === order?._id)

                order?.colli.forEach((collo) => {
                    const colliIndexInRoute = colli?.findIndex((c) => c.barcode === collo.barcode && c.orderId === order._id)

                    if (colliIndexInRoute > -1) {
                        colli[colliIndexInRoute] = {...colli[colliIndexInRoute], ...collo}
                    } else if (collo.barcode === barcode) {
                        collo.orderId = order._id
                        colli.push(collo)
                    }
                })

                if (order && !orderAlreadyInColli) {
                    scannedOrders.push(order)
                }
            }

            if (scannedOrders.length) {
                this.setState({scannedOrders})
            }
        } catch (err) {
            this.setState({error: err.message})
        }

        if (!colli.length) {
            this.setState({loading: false, error: `Geen collo gevonden voor barcode: ${barcode}`})
            return
        }

        if (isScanning) {
            const alreadyScanned = colli.filter((collo) => collo.ready).map((collo) => `${collo.barcode} is al gescand.`)

            const nonReadyColli = colli.filter((collo) => !collo.ready)

            if (!nonReadyColli.length) {
                this.setState({scannedColli: colli, loading: false, error: alreadyScanned.join(' ')})
                return
            }

            const scannedColli = []
            const errors = [...alreadyScanned]

            for (const collo of nonReadyColli) {
                const colloInCurrentRoute = typeof collo.routeIndex === 'number' && !!route?.colli?.[collo.routeIndex]

                if (!colloInCurrentRoute) {
                    scannedColli.push(collo)
                    continue
                }

                await new Promise((resolve) => {
                    OrderActions.setColloReady(collo, true, (err) => {
                        if (err) {
                            errors.push(err)
                        } else {
                            scannedColli.push(collo)
                        }
                        resolve()
                    })
                })
            }

            this.setState({scannedColli, errors, loading: false, message: barcode})
        } else {
            this.setState({scannedColli: colli, loading: false, message: barcode})
        }
    }

    async onClickLoadRoute() {
        try {
            let {route, shift, location} = this.state
            const {reseller} = this.props

            const orderColli = {}

            route.colli.map((collo) => {
                if (!collo.started) {
                    orderColli[collo.orderId] = orderColli[collo.orderId] || []
                    orderColli[collo.orderId].push(collo)
                }
            })

            if (!await this.popup.open('Route inladen', 'Weet je zeker dat je deze route wilt inladen?')) return

            OrderActions.loadRoute(route._id, orderColli, async (err, errors) => {
                if (err) {
                    this.popup.setError(err)
                } else if (errors.length) {
                    this.popup.setErrors(errors)
                } else {
                    this.popup.close()

                    const activityId = reseller.settings.shifts?.activities?.find((activity) => activity.trigger === 'other')?.id

                    if (shift && activityId) {
                        if (!location?.lat) {
                            location = await geolocation.getCurrentPosition(reseller.settings.shifts.gpsRequired)
                        }

                        try {
                            await emit('shifts.startActivity', {id: shift._id, activityId, location})
                        } catch (error) {
                            console.error(error)
                        }
                    }
                }
            })
        } catch (err) {
            await this.popup.setError(err.message || err?.toString())
        }
    }

    async onClickPickupDeliver(address, barcode) {
        const {route} = this.state
        const {history, reseller} = this.props

        // A stop in a bundle opens the whole bundle, with the scanned collo marked as scanned
        const bundle = stopBundles.findBundle(route?.todoItems, address.orderId, address.index)
        const target = bundle || address

        const deviation = utils.nextStopDeviation(reseller, target)

        if (deviation === 'notallowed') {
            this.setState({error: 'Dit adres is niet de volgende stop van de route.'})
        } else if (deviation === 'warning') {
            if (await this.popup.openBoolean('Let op!', `Dit adres is niet de volgende stop van de route. Wil je deze toch nu ${address.type === 'pickup' ? 'ophalen' : 'bezorgen'}?`, questionOptions())) {
                history.push(stopBundles.stopPath(target), {barcode})
            }
        } else {
            history.push(stopBundles.stopPath(target), {barcode})
        }
    }

    render() {
        const {date, shift, scannedColli, scannedOrders, loading, error, errors, warning, message, routes, route, isScanning} = this.state
        const {reseller, branding} = this.props

        const barcodes = []

        const orders = scannedOrders ? structuredClone(scannedOrders) : [route?.orders?.[scannedColli?.[0]?.orderId]]

        scannedColli?.forEach((collo) => {
            const order = route?.orders?.[collo.orderId]

            if (order) {
                if (orders.find((o) => o?._id === order._id)) return

                orders.push(order)
            }
        })

        const addresses = scannedColli?.map((collo) => {
            const order = orders.find((order) => order?._id === collo.orderId)

            const address = order?.addresses[collo.deliveryAddressIndex ?? collo.pickupAddressIndex]

            return address ? {...address, orderId: collo.orderId, barcode: collo.barcode} : null
        }).filter(Boolean).filter((address, addressIndex, self) => {
            const duplicate = self.findIndex((addr) => address.type === addr.type && address.orderId === addr.orderId && address.barcode === addr.barcode)

            return duplicate === addressIndex
        })

        const currentActivity = shift && reseller.settings.shifts?.activities?.find((activity) => activity.id === shift.activityId)
        const shiftStartRequired = (reseller.settings.shifts?.shiftRequired && (!shift || !shift?.started || shift?.paused || shift?.completed)) || currentActivity?.lockAppActivity
        const messengerRouteSettings = resolveMessengerRouteSettings(route, reseller.settings)
        const messengerAddOrderWarning = messengerRouteSettings.messengerAddOrderWarning
        const showWarningOnAddExistingOrder = messengerRouteSettings.showWarningOnAddExistingOrder

        return (
            <>
                {route?.loadingRequiredBeforeStart && !isScanning &&
                    <Panel style={{display: 'flex', width: '100%', padding: '12px 6px 12px 6px', borderTop: 'none', borderRight: 'none', borderLeft: 'none'}}>
                        <Button
                            style={{width: '100%', marginLeft: 42, marginRight: 42}}
                            variant='solid'
                            color={branding?.color || Colors.brandColor}
                            icon='mdi mdi-barcode-scan'
                            label='Route inladen'
                            disabled={date !== time.today() || _.keys(routes).length === 0 || !route.hasUnstartedColli || shiftStartRequired}
                            onClick={async () => {
                                let {location} = this.state

                                this.setState({scannedCollo: null, loading: false, message: '', warning: '', error: ''})
                                OrderActions.startScanning()

                                const activityId = reseller.settings.shifts?.activities?.find((activity) => activity.trigger === 'loading')?.id

                                if (shift && activityId) {
                                    if (!location?.lat) {
                                        location = await geolocation.getCurrentPosition(reseller.settings.shifts.gpsRequired)
                                    }

                                    try {
                                        await emit('shifts.startActivity', {id: shift._id, activityId, location})
                                    } catch (error) {
                                        console.error(error)
                                    }
                                }
                            }}
                        />
                    </Panel>
                }

                {isScanning &&
                    <>
                        <Panel style={{display: 'flex', gap: 6, width: '100%', padding: '12px 6px 12px 6px', borderTop: 'none', borderRight: 'none', borderLeft: 'none'}}>
                            <div style={{width: 36}} />

                            <div style={{flex: 1}}>
                                <Button
                                    variant='solid'
                                    color={branding?.color || Colors.brandColor}
                                    style={{flex: 1}}
                                    label='Inladen afronden'
                                    icon='mdi mdi-check'
                                    onClick={() => this.onClickLoadRoute()}
                                />
                            </div>

                            <div style={{width: 36}}>
                                <IconButton onClick={() => OrderActions.cancelScanning()}>
                                    <i className='mdi mdi-close' />
                                </IconButton>
                            </div>
                        </Panel>

                        <Row style={{height: 50, flexShrink: 0, alignItems: 'center', justifyContent: 'center'}}>
                            <i style={{fontSize: 18, marginRight: 6}} className='mdi mdi-thumb-up-outline' />

                            <P>{`${route.readyColliCount} van ${route.allColliCount} colli zijn gereed om te starten`}</P>
                        </Row>
                    </>
                }

                <ScanFrame
                    maxHeight='30vh'
                    onScan={this.onScan.bind(this)}
                    suggestions={barcodes}
                    disabled={shiftStartRequired}
                    ref={(ref) => this.scanPopup = ref}
                />

                <div style={{flex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'auto'}}>
                    {error &&
                        <Alert style={{width: 'calc(100% - 12px)', marginBottom: 12}} variant='danger'>{error}</Alert>
                    }

                    {errors && errors.length > 0 && errors.map((err, index) => (
                        <Alert key={index} style={{width: 'calc(100% - 12px)', marginBottom: 12}} variant='danger'>{err}</Alert>
                    ))}

                    {warning &&
                        <Alert style={{width: 'calc(100% - 12px)', marginBottom: 12}} variant='warning'>{warning}</Alert>
                    }

                    {message &&
                        <Alert style={{width: 'calc(100% - 12px)', marginBottom: 12}} variant='success'>{message}</Alert>
                    }

                    {scannedColli && addresses &&
                        <>
                            {addresses.toSorted((a, b) => {
                                const scannedOrderA = scannedOrders?.find((order) => order._id === a.orderId)
                                const scannedOrderB = scannedOrders?.find((order) => order._id === b.orderId)

                                const aNr = scannedOrderA ? 1 : 0
                                const bNr = scannedOrderB ? 1 : 0

                                if (aNr !== bNr) {
                                    return aNr - bNr
                                }

                                const typeOrder = {
                                    pickup: 0,
                                    delivery: 1
                                }

                                return (typeOrder[a.type] ?? 99) - (typeOrder[b.type] ?? 99)
                            }).map((address, index) => {
                                const scannedOrder = scannedOrders?.find((order) => order._id === address.orderId)

                                const isLast = index === addresses.length - 1

                                return (
                                    <div key={`scannedColli-address-${index}`} style={{width: 'calc(100% - 12px)', marginBottom: isLast ? 0 : 12, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                        {scannedOrder && (
                                            <H5 style={{alignSelf: 'flex-start', marginBottom: 12}}>Adres niet in route</H5>
                                        )}
                                        {(route?.date && scannedOrder?.date) && route?.date !== scannedOrder?.date && (
                                            <Alert style={{marginBottom: 6}} variant='warning'>Let op: dit is een rit voor {moment(scannedOrder.date).format('DD-MM-YYYY')}</Alert>
                                        )}
                                        <Card style={{width: '100%', flex: 1, marginBottom: 12, padding: '12px 6px 6px 6px'}}>
                                            <Row>
                                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', alignItems: 'center', marginRight: 6, minWidth: 'auto'}}>
                                                    <H4 translate='no' style={{color: (scannedOrder ? scannedOrder.routeColor : route?.color) || Colors.textDark, marginBottom: 6}}>
                                                        {`${scannedOrder ? scannedOrder.routeName || 'Geen route' : route.name}${scannedOrder ? (scannedOrder.routeStartTime ? ` (${scannedOrder.routeStartTime})` : '') : route.plannedStartTime ? ` (${route.plannedStartTime})` : ''}`}
                                                    </H4>
                                                    <H2 translate='no' style={{marginBottom: 30, color: (scannedOrder ? scannedOrder.routeColor : route?.color) || Colors.textDark}}>
                                                        {address.stopNumber ? address.stopNumber : '-'}
                                                    </H2>
                                                </Row>

                                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', marginLeft: 6, minWidth: 'auto'}}>
                                                    <div style={{flex: 1, minWidth: 0}}>

                                                        <S2>{`${address.type === 'pickup' ? 'Ophaaladres' : 'Bezorgadres'}`}</S2>

                                                        <P translate='no'>{`${address.name || ''}`}</P>
                                                        <P translate='no'>{address.attention}</P>
                                                        <P translate='no'>{`${address.street} ${address.nr}${address.addition}`}</P>
                                                        <P translate='no'>{address.street2}</P>
                                                        <P translate='no'>{`${address.postalCode} ${address.city}`}</P>
                                                        <P translate='no' style={{whiteSpace: 'pre-line'}}>{address.instructions}</P>

                                                        <div style={{display: 'flex', alignItems: 'center'}}>
                                                            {address.gpsRequired &&
                                                                <i style={{fontSize: 20, marginRight: 3}} className='mdi mdi-map-marker' />
                                                            }
                                                            {address.signatureRequired &&
                                                                <i style={{fontSize: 20, marginRight: 3}} className='mdi mdi-fountain-pen-tip' />
                                                            }
                                                            {address.nameRequired && !address.signatureRequired &&
                                                                <i style={{fontSize: 20, marginRight: 3}} className='mdi mdi-signature-text' />
                                                            }
                                                            {address.statedAddressOnly &&
                                                                <i style={{fontSize: 20, marginRight: 3}} className='mdi mdi-home' />
                                                            }
                                                            {address.mailboxDelivery &&
                                                                <i style={{fontSize: 20, marginRight: 3}} className='mdi mdi-mailbox-open-up' />
                                                            }
                                                            {address.scanRequired &&
                                                                <i style={{fontSize: 20, marginRight: 3}} className='mdi mdi-barcode-scan' />
                                                            }
                                                            {address.imageRequired &&
                                                                <i style={{fontSize: 20}} className='mdi mdi-camera' />
                                                            }
                                                            {address.minimumAge &&
                                                                <div style={{fontSize: 14, fontWeight: 500, marginRight: 6}}>{`${address.minimumAge}+`}</div>
                                                            }
                                                            {address.idCheck &&
                                                                <i style={{fontSize: 20, marginRight: 3}} className='mdi mdi-card-account-details-outline' />
                                                            }
                                                            {address.verificationCode &&
                                                                <i style={{fontSize: 20}} className='mdi mdi-shield-key-outline' />
                                                            }
                                                        </div>
                                                    </div>

                                                    <div style={{flex: 1, marginTop: 12}}>
                                                        {address.hasArrived && address.timeOfArrival &&
                                                            <>
                                                                <S2>Aankomsttijd</S2>
                                                                <P translate='no'>{`${address.timeOfArrival.replace(':', '.')} uur`}</P>
                                                            </>
                                                        }

                                                        {!address.hasArrived && address.plannedTimeOfArrival &&
                                                            <>
                                                                <S2>Verwachte aankomsttijd</S2>
                                                                <P translate='no'>{`${address.plannedTimeOfArrival.replace(':', '.')} uur`}</P>
                                                            </>
                                                        }

                                                        {!address.hasArrived && !address.plannedTimeOfArrival &&
                                                            <>
                                                                <S2>Tijdvak</S2>
                                                                <P translate='no'>{`${address.startTime.replace(':', '.')} - ${address.endTime.replace(':', '.')} uur`}</P>
                                                            </>
                                                        }
                                                    </div>
                                                </Row>
                                            </Row>
                                        </Card>

                                        {!isScanning && !shiftStartRequired && scannedColli && address && !address.hasArrived && utils.nextStopDeviation(reseller, address) !== 'notallowed' && !scannedOrder &&
                                            <Row style={{flexShrink: 0, marginBottom: 6}}>
                                                <Button
                                                    style={{width: '100%'}}
                                                    size='xl'
                                                    variant='solid'
                                                    color={branding?.color || Colors.brandColor}
                                                    icon='mdi mdi-arrow-right-bold-circle'
                                                    label='Adres openen'
                                                    onClick={() => this.onClickPickupDeliver(address, scannedColli[0].barcode)}
                                                    disabled={!route.started}
                                                />
                                            </Row>
                                        }

                                        {scannedOrder && canAddOrderToRouteWithWarningSetting(messengerAddOrderWarning, scannedOrder, route) && `${scannedOrder.routeId || ''}` !== `${route?._id || ''}` &&
                                            <Button
                                                variant='outline'
                                                icon='mdi mdi-plus-circle'
                                                label='Toevoegen aan route'
                                                loading={loading}
                                                onClick={async () => {
                                                    try {
                                                        let closePopup = false
                                                        if ((route?.date && scannedOrder?.date) && route?.date !== scannedOrder?.date) {
                                                            if (!await this.popup.open('Rit toevoegen', `Je gaat de rit verplaatsen van ${moment(scannedOrder.date).format('DD-MM-YYYY')} naar vandaag, wil je doorgaan?`)) return
                                                            closePopup = true
                                                        }

                                                        if (scannedOrder.colli.length > 1) {
                                                            if (!await this.popup.open('Rit toevoegen', `De rit waar deze collo in zit bevat in totaal ${scannedOrder.colli.length} colli. Deze worden allemaal toegevoegd aan jouw route. Weet je zeker dat je door wil gaan?`)) return
                                                            closePopup = true
                                                        }

                                                        if (showWarningOnAddExistingOrder) {
                                                            if (!await this.popup.open('Rit toevoegen', 'De rit waar deze collo toe behoort wordt toegevoegd aan je route. Weet je zeker dat je door wil gaan?')) return
                                                            closePopup = true
                                                        }

                                                        await emit('orders.addToRoute', {ids: [scannedOrder._id], routeId: route._id, lockRouteAfterPlanning: false, rescheduleOrderIfNeeded: true})

                                                        if (closePopup) {
                                                            this.popup.close()
                                                        }

                                                        // Wait for socket update and rescan to set collo to ready
                                                        setTimeout(async () => {
                                                            try {
                                                                await emit('orders.scan', {id: scannedOrder._id, barcode: scannedColli[0].barcode})
                                                                await this.onScan(scannedColli[0].barcode, true, [scannedOrder._id])
                                                            } catch (err) {
                                                                this.setState({error: err.message})
                                                            }
                                                        }, 200)
                                                    } catch (err) {
                                                        this.popup.close()
                                                        this.setState({error: err.message})
                                                    }
                                                }}
                                                style={{marginBottom: 6}}
                                            />
                                        }
                                    </div>
                                )
                            })}
                        </>
                    }
                </div>

                <Popup ref={(ref) => this.popup = ref} />
            </>
        )
    }
}

export default Scan
